import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import analyticsTracker from '../utils/analyticsTracker';
// Styles consolidated into index.css

const API = process.env.REACT_APP_BACKEND_URL;

// AskStewie logo - local character image
const STEWIE_LOGO = "/stewie_front.png";

function JobBriefingPage({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [jobBrief, setJobBrief] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');
  const [viewOnly, setViewOnly] = useState(false);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start new conversation on mount
  useEffect(() => {
    analyticsTracker.trackPageView('job_briefing');
    analyticsTracker.trackJobBriefingStarted();

    const state = location.state;

    // View completed briefing (read-only)
    if (state?.viewCompleted) {
      const b = state.viewCompleted;
      setViewOnly(true);
      setIsComplete(true);
      setJobBrief(b.job_brief);
      setProgress(100);
      setCurrentStep('complete');
      setSessionId(b.session_id);
      if (b.messages && b.messages.length > 0) {
        setMessages(b.messages);
      }
      return;
    }

    // Resume draft: reload session messages and continue
    if (state?.resume) {
      const b = state.resume;
      if (b.messages && b.messages.length > 0) {
        setMessages(b.messages);
      }
      setProgress(b.progress_percent || 0);
      setCurrentStep(b.current_step || 'job_identification');
      // Start a fresh session and replay (server is stateless per session)
      startConversation(state.resume);
      return;
    }

    // Template: start conversation then auto-send prefilled task
    if (state?.template) {
      startConversationWithTemplate(state.template);
      return;
    }

    startConversation();
  }, []);

  const startConversation = async (resumeData) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/api/job-briefing/start`);
      setSessionId(response.data.session_id);
      setCurrentStep(response.data.current_step);
      setProgress(response.data.progress_percent);
      setMessages([{
        role: 'assistant',
        content: response.data.assistant_message
      }]);
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      setError('Failed to start conversation. Please try again.');
      console.error('Start error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const startConversationWithTemplate = async (template) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/api/job-briefing/start`);
      setSessionId(response.data.session_id);
      setMessages([{
        role: 'assistant',
        content: response.data.assistant_message
      }]);
      // Auto-send the prefilled task from the template
      const prefillMsg = template.prefill?.job_task || template.name;
      setTimeout(() => sendMessage(prefillMsg, response.data.session_id), 400);
    } catch (err) {
      setError('Failed to start conversation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveBriefing = async (sid, status, brief, step, pct, msgs) => {
    try {
      const tplId = location.state?.template?.template_id || null;
      await axios.post(`${API}/api/job-briefing/save`, {
        session_id: sid,
        status,
        job_brief: brief || null,
        current_step: step,
        progress_percent: pct,
        messages: msgs.map(m => ({ role: m.role, content: m.content })),
        template_id: tplId,
      });
    } catch (err) {
      console.error('Save briefing error:', err);
    }
  };

  const sendMessage = async (messageText = inputValue, sessionOverride = null) => {
    const sid = sessionOverride || sessionId;
    if (!messageText.trim() || isLoading || !sid) return;

    const userMessage = { role: 'user', content: messageText.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError('');
    setSuggestions([]);
    analyticsTracker.trackFeatureUse('job_briefing', 'send_message');

    try {
      const response = await axios.post(`${API}/api/job-briefing/chat`, {
        session_id: sid,
        message: messageText.trim()
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.assistant_message
      };
      setCurrentStep(response.data.current_step);
      setProgress(response.data.progress_percent);
      setIsComplete(response.data.is_complete);
      setSuggestions(response.data.suggestions || []);

      if (response.data.current_step) {
        analyticsTracker.trackJobBriefingStepCompleted(response.data.current_step);
      }

      if (response.data.is_complete) {
        analyticsTracker.trackJobBriefingCompleted();
        // Auto-save completed briefing
        setMessages(prev => {
          const allMsgs = [...prev, assistantMessage];
          saveBriefing(sid, 'completed', response.data.job_brief, 'complete', 100, allMsgs);
          return allMsgs;
        });
      } else {
        setMessages(prev => [...prev, assistantMessage]);
      }
      
      if (response.data.job_brief) {
        setJobBrief(response.data.job_brief);
      }

    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(prev => prev ? `${prev}, ${suggestion}` : suggestion);
    inputRef.current?.focus();
  };

  const handleQuickConfirm = () => {
    sendMessage('Confirmed');
  };

  const handleStartOver = () => {
    setMessages([]);
    setSessionId(null);
    setJobBrief(null);
    setIsComplete(false);
    setProgress(0);
    setSuggestions([]);
    startConversation();
  };

  const handlePrint = () => {
    analyticsTracker.trackJobBriefingExported();
    window.print();
  };

  const renderMessage = (msg, idx) => {
    const isAssistant = msg.role === 'assistant';
    return (
      <div 
        key={idx} 
        className={`chat-message ${isAssistant ? 'assistant' : 'user'}`}
        data-testid={`message-${idx}`}
      >
        <div className="message-avatar">
          {isAssistant ? (
            <img src={STEWIE_LOGO} alt="AskStewie" className="stewie-avatar" />
          ) : (
            '👤'
          )}
        </div>
        <div className="message-content">
          <div className="message-bubble">
            {msg.content.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line.startsWith('**') && line.endsWith('**') ? (
                  <strong>{line.slice(2, -2)}</strong>
                ) : line.startsWith('• ') ? (
                  <div className="list-item">{line}</div>
                ) : line.startsWith('═') ? (
                  <hr className="divider" />
                ) : line.startsWith('_') && line.endsWith('_') ? (
                  <em className="footnote">{line.slice(1, -1)}</em>
                ) : (
                  line
                )}
                {i < msg.content.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getStepLabel = (step) => {
    const labels = {
      'job_identification': 'Job Details',
      'location': 'Location',
      'equipment': 'Equipment',
      'scope': 'Scope of Work',
      'hazards': 'Hazards',
      'energy_isolation': 'Energy Isolation',
      'ppe': 'PPE',
      'crew': 'Crew',
      'environment': 'Environment',
      'emergency': 'Emergency Plan',
      'fatal_risk': 'Fatal Risk',
      'validation': 'Validation',
      'complete': 'Complete'
    };
    return labels[step] || step;
  };

  return (
    <div className="job-briefing-chat-page" data-testid="job-briefing-page">
      {/* Header */}
      <header className="jb-header">
        <div className="header-content">
          <div className="header-badge">Safety Assistant</div>
          <h1>Job Brief Builder</h1>
          <p>Conversational job briefing with hazard identification</p>
        </div>
        {!isComplete && !viewOnly && sessionId && (
          <button
            className="back-btn"
            style={{ marginLeft: 'auto', background: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.3)', color: '#f59e0b' }}
            onClick={() => {
              saveBriefing(sessionId, 'draft', null, currentStep, progress, messages);
              navigate('/job-briefing');
            }}
            data-testid="save-draft-btn"
          >
            Save Draft
          </button>
        )}
      </header>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
            data-testid="progress-fill"
          />
        </div>
        <div className="progress-label">
          <span className="step-label">{getStepLabel(currentStep)}</span>
          <span className="progress-percent">{progress}%</span>
        </div>
      </div>

      {/* Chat Container */}
      <div className="chat-container">
        <div className="messages-container" data-testid="messages-container">
          {messages.map((msg, idx) => renderMessage(msg, idx))}
          
          {isLoading && (
            <div className="chat-message assistant loading">
              <div className="message-avatar">
                <img src={STEWIE_LOGO} alt="AskStewie" className="stewie-avatar" />
              </div>
              <div className="message-content">
                <div className="message-bubble typing">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && !isComplete && (
          <div className="suggestions-container" data-testid="suggestions">
            <div className="suggestions-label">Suggested items (click to add):</div>
            <div className="suggestions-list">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(suggestion)}
                  data-testid={`suggestion-${idx}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <button 
              className="quick-confirm-btn"
              onClick={handleQuickConfirm}
              data-testid="confirm-btn"
            >
              ✓ Confirm All
            </button>
          </div>
        )}

        {/* Input Area */}
        {!isComplete && (
          <div className="input-container">
            {error && <div className="error-message">{error}</div>}
            <div className="input-row">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your response..."
                className="chat-input"
                disabled={isLoading}
                rows={2}
                data-testid="chat-input"
              />
              <button
                className="send-btn"
                onClick={() => sendMessage()}
                disabled={isLoading || !inputValue.trim()}
                data-testid="send-btn"
              >
                {isLoading ? '...' : '→'}
              </button>
            </div>
          </div>
        )}

        {/* Complete Actions */}
        {isComplete && (
          <div className="complete-section">
            {/* Formatted Job Brief Card */}
            {jobBrief && (
              <div className="job-brief-card" data-testid="job-brief-card">
                <div className="job-brief-header">
                  <h2>📋 Job Brief Summary</h2>
                  <span className="generated-time">
                    Generated: {new Date(jobBrief.generated_at).toLocaleString()}
                  </span>
                </div>

                <div className="job-brief-section">
                  <h3>Job Details</h3>
                  <div className="brief-field">
                    <label>Task:</label>
                    <span>{jobBrief.job_summary}</span>
                  </div>
                  <div className="brief-field">
                    <label>Location:</label>
                    <span>{jobBrief.location}</span>
                  </div>
                </div>

                <div className="job-brief-section">
                  <h3>Equipment</h3>
                  <div className="brief-grid">
                    <div className="brief-field">
                      <label>Type:</label>
                      <span>{jobBrief.equipment?.type || 'Not specified'}</span>
                    </div>
                    <div className="brief-field">
                      <label>Voltage:</label>
                      <span>{jobBrief.equipment?.voltage || 'Not specified'}</span>
                    </div>
                    <div className="brief-field">
                      <label>Status:</label>
                      <span className={`status-badge ${jobBrief.equipment?.status === 'de-energized' ? 'safe' : 'caution'}`}>
                        {jobBrief.equipment?.status || 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>

                {jobBrief.scope_of_work?.length > 0 && (
                  <div className="job-brief-section">
                    <h3>Scope of Work</h3>
                    <ul className="brief-list">
                      {jobBrief.scope_of_work.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {jobBrief.hazards?.length > 0 && (
                  <div className="job-brief-section hazards-section">
                    <h3>⚠️ Identified Hazards</h3>
                    <div className="hazard-tags">
                      {jobBrief.hazards.map((hazard, idx) => (
                        <span key={idx} className="hazard-tag">{hazard}</span>
                      ))}
                    </div>
                  </div>
                )}

                {jobBrief.controls?.length > 0 && (
                  <div className="job-brief-section">
                    <h3>Controls</h3>
                    <div className="controls-list">
                      {jobBrief.controls.slice(0, 5).map((ctrl, idx) => (
                        <div key={idx} className="control-item">
                          <span className="control-hazard">{ctrl.hazard}:</span>
                          <span className="control-measure">{ctrl.control}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="job-brief-section">
                  <h3>Energy Isolation</h3>
                  <div className="isolation-checks">
                    <div className={`check-item ${jobBrief.energy_isolation?.clearance_received ? 'checked' : 'unchecked'}`}>
                      {jobBrief.energy_isolation?.clearance_received ? '✓' : '✗'} Clearance Received
                    </div>
                    <div className={`check-item ${jobBrief.energy_isolation?.switching_completed ? 'checked' : 'unchecked'}`}>
                      {jobBrief.energy_isolation?.switching_completed ? '✓' : '✗'} Switching Completed
                    </div>
                    <div className={`check-item ${jobBrief.energy_isolation?.grounds_applied ? 'checked' : 'unchecked'}`}>
                      {jobBrief.energy_isolation?.grounds_applied ? '✓' : '✗'} Grounds Applied
                    </div>
                  </div>
                </div>

                {jobBrief.ppe?.length > 0 && (
                  <div className="job-brief-section">
                    <h3>🦺 Required PPE</h3>
                    <div className="ppe-grid">
                      {jobBrief.ppe.map((item, idx) => (
                        <span key={idx} className="ppe-item">✓ {item}</span>
                      ))}
                    </div>
                  </div>
                )}

                {jobBrief.crew?.leader && (
                  <div className="job-brief-section">
                    <h3>Crew</h3>
                    <div className="brief-field">
                      <label>Leader:</label>
                      <span className="crew-leader">{jobBrief.crew.leader}</span>
                    </div>
                    {jobBrief.crew.info && (
                      <div className="brief-field">
                        <label>Team:</label>
                        <span>{jobBrief.crew.info}</span>
                      </div>
                    )}
                  </div>
                )}

                {jobBrief.primary_fatal_risk && (
                  <div className="job-brief-section fatal-risk-section">
                    <h3>🔴 Primary Fatal Risk</h3>
                    <div className="fatal-risk-content">
                      {jobBrief.primary_fatal_risk}
                    </div>
                  </div>
                )}

                {jobBrief.stop_work_triggers?.length > 0 && (
                  <div className="job-brief-section">
                    <h3>🛑 Stop Work Triggers</h3>
                    <ul className="stop-work-list">
                      {jobBrief.stop_work_triggers.slice(0, 5).map((trigger, idx) => (
                        <li key={idx}>{trigger}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="job-brief-disclaimer">
                  ⚠️ {jobBrief.disclaimer}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="complete-actions">
              <button 
                className="action-btn primary"
                onClick={handlePrint}
                data-testid="print-btn"
              >
                🖨️ Print Job Brief
              </button>
              <button 
                className="action-btn secondary"
                onClick={handleStartOver}
                data-testid="start-over-btn"
              >
                🔄 Start New Brief
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default JobBriefingPage;
